import * as THREE from "three";
import type { RenderGroupAppearance } from "./scene-contract";

export type AdvancedGemstoneMaterialInfo = {
  shaderModel: "mesh-physical-v1";
  unsupported: string[];
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function colorWithInternalStrength(color: string, strength: number) {
  const base = new THREE.Color(color);
  const lightened = base.clone().lerp(new THREE.Color("#ffffff"), clamp(1 - strength, 0, 0.55));
  return lightened;
}

export function createAdvancedGemstoneMaterial(appearance: RenderGroupAppearance) {
  const outerMix = clamp(appearance.outerReflectionMix, 0, 1);
  const maxOuter = clamp(appearance.maxOuterReflection, 0, 1);
  const internalStrength = clamp(appearance.internalColorStrength, 0, 2);
  const refractionCount = Math.max(1, Math.round(appearance.refractionCount));
  const refractionDepth = clamp(appearance.refractionDepth, 0, 3);
  const color = colorWithInternalStrength(appearance.color, internalStrength);
  const attenuationColor = colorWithInternalStrength(appearance.attenuationColor || appearance.color, internalStrength);

  const material = new THREE.MeshPhysicalMaterial({
    color,
    metalness: 0,
    roughness: clamp(appearance.roughness, 0, 1),
    clearcoat: maxOuter,
    clearcoatRoughness: clamp(appearance.roughness * (1 - outerMix), 0.02, 0.35),
    envMapIntensity: appearance.envMapIntensity * (1 + outerMix * 0.45),
    transmission: clamp(appearance.transmission, 0, 1),
    ior: clamp(appearance.ior, 1, 2.6),
    thickness: Math.max(appearance.thickness, refractionDepth),
    attenuationColor,
    attenuationDistance: Math.max(0.01, appearance.attenuationDistance),
    dispersion: clamp(appearance.dispersion, 0, 1),
    iridescence: clamp(appearance.dispersion * 0.45, 0, 0.35),
    iridescenceIOR: clamp(appearance.ior, 1, 2.33),
    iridescenceThicknessRange: [80, 80 + refractionCount * 90],
    specularIntensity: clamp(0.7 + maxOuter * 0.6, 0, 1.4),
    specularColor: new THREE.Color("#ffffff").lerp(color, clamp(internalStrength * 0.18, 0, 0.35)),
    transparent: appearance.transparent || appearance.transmission > 0.05,
    opacity: 1,
    depthWrite: appearance.transmission < 0.65,
    side: THREE.DoubleSide,
  });

  material.userData.advancedGemstone = {
    shaderModel: "mesh-physical-v1",
    refractionCount,
    refractionDepth,
    outerReflectionMix: outerMix,
    maxOuterReflection: maxOuter,
    internalColorStrength: internalStrength,
    unsupported: [
      "BVH internal ray bounce shader",
      "true mixed outer reflection shader branch",
    ],
  } satisfies AdvancedGemstoneMaterialInfo & Record<string, unknown>;

  return material;
}
